import { type Middleware } from '@bmai/auth-api-client';
import { applyCsrfHeader, isCsrfMismatch } from './auth/CsrfToken';
import { SessionExpiryNotifier } from './auth/SessionExpiryNotifier';

/** Endpoints whose 401 means "not logged in" — not "session expired". The
 *  caller decides what to render; we must not trigger a redirect loop. */
function isAuthProbe(url: string): boolean {
    return url.includes('/api/auth/') || url.includes('/api/me');
}

type ApiKeys<M> = {
    [K in keyof M]: K extends `${string}Api` ? K : never;
}[keyof M];

type Apis<M, C> = {
    [K in ApiKeys<M> as Uncapitalize<K & string>]:
        M[K] extends new (configuration?: C) => infer R ? R : never;
};

export class ApiClientFactory {
    private static readonly cookieMiddleware: Middleware = {
        pre: async ({ url, init }) => {
            const headers = applyCsrfHeader(init.method, new Headers(init.headers));
            return { url, init: { ...init, headers, credentials: 'include' } };
        },
        post: async ({ url, init, response }) => {
            let res = response;
            if (await isCsrfMismatch(res)) {
                const headers = applyCsrfHeader(init.method, new Headers(init.headers));
                res = await fetch(url, { ...init, headers, credentials: 'include' });
                if (await isCsrfMismatch(res)) {
                    SessionExpiryNotifier.notifyExpired();
                    return res;
                }
            }
            if (res.status === 401 && !isAuthProbe(url)) {
                SessionExpiryNotifier.notifyExpired();
            }
            return res;
        },
    };

    static createConfig(basePath: string): { basePath: string; middleware: Middleware[] } {
        return { basePath, middleware: [ApiClientFactory.cookieMiddleware] };
    }

    static instantiateApis<M extends object, C>(module: M, configuration: C): Apis<M, C> {
        const out: Record<string, unknown> = {};
        for (const [name, exported] of Object.entries(module)) {
            if (!name.endsWith('Api') || typeof exported !== 'function') continue;
            const key = name.charAt(0).toLowerCase() + name.slice(1);
            out[key] = new (exported as new (configuration: C) => unknown)(configuration);
        }
        return out as Apis<M, C>;
    }
}
